/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { StateUpdater, useEffect, useState } from 'preact/hooks';
import { Props } from '../PRIVATE_GridView';
import { DataGridCell } from './DataGridCell';
import { renderGrid } from './utils/DataGridRenderUtils';
import { DataGridColumnRange, DataGridRowRange } from '#PRIVATE_GridView/DataGrid.types';
/**
 * Component that renders a basic grid.
 */
export const DataGrid = function DataGrid<D>(props: Props<D>) {
  const [skeletonList, setSkeletonList] = useState<
    { type: string; rowIndex?: number; level: number; columnIndex?: number }[]
  >([]);
  const { grid, classes, style, cellHeight, cellWidth } = renderGrid(props, skeletonList);

  const calculateViewportRange = (
    element: HTMLElement,
    cellSize: number,
    totalCount: number,
    isVertical: boolean
  ) => {
    const totalInViewPort = Math.round(
      (isVertical ? element.offsetHeight : element.offsetWidth) / cellSize
    );
    const firstPosition = Math.round(
      (isVertical ? element.scrollTop : element.scrollLeft) / cellSize
    );
    const buffer = Math.round(totalInViewPort / 2);

    const tempTopPosition = firstPosition - buffer;
    const topPosition = tempTopPosition > 0 ? tempTopPosition : 0;

    const tempBottomPosition = topPosition + totalInViewPort + buffer * 2;
    const bottomPosition = tempBottomPosition < totalCount ? tempBottomPosition : totalCount;

    return { topPosition, bottomPosition };
  };

  const generateSkeletonsForHeaders = (
    levels: number | undefined,
    ranges: DataGridRowRange[] | DataGridColumnRange[] | undefined,
    topViewport: number,
    bottomViewport: number,
    type: string,
    tempSkeletonList: {
      type: string;
      rowIndex?: number;
      level?: number;
      columnIndex?: number;
    }[],
    isRow = true
  ) => {
    if (ranges && levels) {
      for (let level = 0; level < levels; level++) {
        const skeletonArray = new Array(bottomViewport).fill(null);
        ranges.forEach((range) => {
          const start = isRow
            ? (range as DataGridRowRange).rowStart
            : (range as DataGridColumnRange).columnStart;
          const count = isRow
            ? (range as DataGridRowRange).rowCount
            : (range as DataGridColumnRange).columnCount;

          for (let i = start; i < start + count; i++) {
            if (i < skeletonArray.length) {
              skeletonArray[i] = true;
            }
          }
        });

        for (let i = topViewport; i < skeletonArray.length; i++) {
          if (!skeletonArray[i]) {
            tempSkeletonList.push({
              type: `${type}Skeleton`,
              [isRow ? 'rowIndex' : 'columnIndex']: i,
              level: level
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    const element = document.getElementById('dataGrid') as HTMLDivElement;

    const { topPosition: topRowOfViewPort, bottomPosition: bottomRowOfViewPort } =
      calculateViewportRange(element, cellHeight, props.totalRowCount as number, true);

    const { topPosition: topColumnOfViewPort, bottomPosition: bottomColumnOfViewPort } =
      calculateViewportRange(element, cellWidth, props.totalColumnCount as number, false);
    const tempSkeletonList: StateUpdater<
      { type: string; rowIndex?: number; level: number; columnIndex?: number }[]
    > = [];

    generateSkeletonsForHeaders(
      props.header?.row?.levels,
      props.ranges.rowHeaders,
      topRowOfViewPort,
      bottomRowOfViewPort,
      'rowHeader',
      tempSkeletonList,
      true
    );

    generateSkeletonsForHeaders(
      props.header?.rowEnd?.levels,
      props.ranges.rowEndHeaders,
      topRowOfViewPort,
      bottomRowOfViewPort,
      'rowEndHeader',
      tempSkeletonList,
      true
    );

    generateSkeletonsForHeaders(
      props.header?.column?.levels,
      props.ranges.columnHeaders,
      topColumnOfViewPort,
      bottomColumnOfViewPort,
      'columnHeader',
      tempSkeletonList,
      false
    );

    generateSkeletonsForHeaders(
      props.header?.columnEnd?.levels,
      props.ranges.columnEndHeaders,
      topColumnOfViewPort,
      bottomColumnOfViewPort,
      'columnEndHeader',
      tempSkeletonList,
      false
    );

    if (props.ranges.dataBody) {
      const skeletonArray = new Array(bottomColumnOfViewPort)
        .fill(null)
        .map(() => new Array(bottomRowOfViewPort).fill(null));

      props.ranges.dataBody.forEach((range) => {
        for (let i = range.columnStart; i < range.columnStart + range.columnCount; i++) {
          if (i < skeletonArray.length) {
            for (let j = range.rowStart; j < range.rowStart + range.rowCount; j++) {
              if (j < skeletonArray[i].length) {
                skeletonArray[i][j] = true;
              }
            }
          }
        }
      });

      for (let i = topColumnOfViewPort; i < skeletonArray.length; i++) {
        for (let j = topRowOfViewPort; j < skeletonArray[i].length; j++) {
          if (!skeletonArray[i][j]) {
            tempSkeletonList.push({ type: 'cellSkeleton', columnIndex: i, rowIndex: j, level: 0 });
          }
        }
      }
    }
    setSkeletonList(tempSkeletonList);
  }, [cellHeight, cellWidth, props]);

  return (
    <div id="dataGrid" role={'application'} class={classes} style={style}>
      {grid.map((gridCell) => (
        <DataGridCell<D> cell={gridCell} height={cellHeight} width={cellWidth} />
      ))}
    </div>
  );
};
